import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  HostListener,
  inject,
  Injector,
  signal,
  untracked,
  viewChild
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Check, CheckCheck, LoaderCircle, LucideAngularModule, SendHorizontal } from 'lucide-angular';
import { IChatMessage } from '../../../interfaces/chat.interface';
import { ChatService } from '../../../services/chat/chat.service';
import { groupMessagesByDay, parseChatDate } from '../../../services/chat/chat-utils';
import { IconCloseComponent } from '../../icons/icon-close/icon-close.component';

interface IDayGroup {
  day: string;
  /** Clé i18n (aujourd'hui / hier) ou null : on affiche alors `label`. */
  labelKey: string | null;
  label: string;
  messages: IChatMessage[];
}

/** Distance (px) au bas sous laquelle on considère être « en bas » de la liste. */
const NEAR_BOTTOM_PX = 80;

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

function dayKey(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

@Component({
  selector: 'braidsbook-chat-panel',
  imports: [TranslatePipe, LucideAngularModule, IconCloseComponent],
  templateUrl: './chat-panel.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: `
    .chat-backdrop { animation: chat-fade 200ms ease-out; }
    .chat-drawer { animation: chat-slide 250ms cubic-bezier(0.22, 1, 0.36, 1); }
    .chat-dot { animation: chat-bounce 1.2s infinite ease-in-out; }
    .chat-dot:nth-child(2) { animation-delay: 0.15s; }
    .chat-dot:nth-child(3) { animation-delay: 0.3s; }
    @keyframes chat-fade { from { opacity: 0; } }
    @keyframes chat-slide { from { transform: translateX(100%); } }
    @keyframes chat-bounce { 0%, 80%, 100% { opacity: .3; transform: translateY(0); } 40% { opacity: 1; transform: translateY(-3px); } }
    @media (prefers-reduced-motion: reduce) {
      .chat-backdrop, .chat-drawer, .chat-dot { animation: none; }
    }
  `
})
export class ChatPanelComponent {
  readonly #chatService = inject(ChatService);
  readonly #translate = inject(TranslateService);
  readonly #injector = inject(Injector);

  protected readonly icons = { Check, CheckCheck, LoaderCircle, SendHorizontal };
  protected readonly titleId = 'braidsbook-chat-panel-title';

  readonly isOpen = this.#chatService.isOpen;
  readonly context = this.#chatService.context;
  readonly messages = this.#chatService.messages;
  readonly loading = this.#chatService.loading;
  readonly error = this.#chatService.error;
  readonly sendError = this.#chatService.sendError;
  readonly sending = this.#chatService.sending;
  readonly hasMore = this.#chatService.hasMore;
  readonly loadingOlder = this.#chatService.loadingOlder;
  readonly isContactTyping = this.#chatService.isContactTyping;

  readonly draft = signal('');
  readonly canSend = computed(
    () => this.draft().trim().length > 0 && !this.sending() && !this.loading() && !this.error()
  );

  readonly #lang = toSignal(this.#translate.onLangChange.pipe(map(e => e.lang)), {
    initialValue: this.#translate.getCurrentLang()
  });
  readonly #locale = computed(() => (this.#lang() === 'en' ? 'en' : 'fr'));

  readonly initial = computed(() => (this.context()?.contactName?.trim().charAt(0) ?? '').toUpperCase());

  /** « Coiffure · date, heure » dans la langue courante. */
  readonly reservationLine = computed(() => {
    const ctx = this.context();
    if (!ctx) return '';
    const parts: string[] = [];
    if (ctx.hairstyleName) parts.push(ctx.hairstyleName);
    if (ctx.date) {
      const d = parseChatDate(ctx.date + (ctx.hour ? ` ${ctx.hour}:00` : ' 00:00:00'));
      if (!isNaN(d.getTime())) {
        const date = d.toLocaleDateString(this.#locale(), { day: 'numeric', month: 'short', year: 'numeric' });
        parts.push(ctx.hour ? `${date}, ${ctx.hour}` : date);
      }
    } else if (ctx.hour) {
      parts.push(ctx.hour);
    }
    return parts.join(' · ');
  });

  readonly groups = computed<IDayGroup[]>(() => {
    const locale = this.#locale();
    const now = new Date();
    const today = dayKey(now);
    const yesterday = dayKey(new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1));
    return groupMessagesByDay(this.messages()).map(group => ({
      ...group,
      labelKey: group.day === today ? 'CHAT.TODAY' : group.day === yesterday ? 'CHAT.YESTERDAY' : null,
      label: parseChatDate(`${group.day} 00:00:00`).toLocaleDateString(locale, {
        weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
      })
    }));
  });

  private readonly composerRef = viewChild<ElementRef<HTMLTextAreaElement>>('composer');
  private readonly scrollerRef = viewChild<ElementRef<HTMLElement>>('scroller');

  #nearBottom = true;
  #lastMessageId: string | null = null;
  /** Réservation à laquelle appartient le brouillon (null : panneau fermé). */
  #draftKey: number | null = null;

  constructor() {
    // Panneau unique : le brouillon ne doit pas passer d'une conversation à l'autre.
    effect(() => {
      const key = this.isOpen() ? (this.context()?.reservationId ?? null) : null;
      untracked(() => {
        if (key === this.#draftKey) return;
        this.#draftKey = key;
        this.#resetDraft();
      });
    });

    // À l'ouverture : focus sur la saisie et défilement en bas.
    effect(() => {
      if (!this.isOpen()) {
        this.#nearBottom = true;
        this.#lastMessageId = null;
        return;
      }
      untracked(() => this.#afterRender(() => {
        this.composerRef()?.nativeElement.focus();
        this.#scrollToBottom();
      }));
    });

    // Nouveau message en bas (ou indicateur de saisie) : on suit si on était déjà en bas.
    // Les messages anciens ajoutés en haut ne changent pas le dernier id : pas de défilement.
    effect(() => {
      const last = this.messages().at(-1)?.id ?? null;
      const typing = this.isContactTyping();
      if (!untracked(this.isOpen)) return;
      const changed = last !== this.#lastMessageId;
      this.#lastMessageId = last;
      if ((changed || typing) && this.#nearBottom) {
        untracked(() => this.#afterRender(() => this.#scrollToBottom()));
      }
    });
  }

  @HostListener('document:keydown.escape', ['$event'])
  onEscape(event?: KeyboardEvent): void {
    // Échap pendant une composition IME annule la composition, pas le panneau.
    if (event?.isComposing || event?.keyCode === 229) return;
    if (this.isOpen()) this.close();
  }

  close(): void {
    this.#chatService.close();
  }

  retry(): void {
    void this.#chatService.retry();
  }

  isMine(m: IChatMessage): boolean {
    const me = this.#chatService.currentUserId();
    return me !== null && String(m.senderId) === String(me);
  }

  time(m: IChatMessage): string {
    const d = parseChatDate(m.createdAt);
    return isNaN(d.getTime()) ? '' : `${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }

  onScroll(): void {
    const el = this.scrollerRef()?.nativeElement;
    if (el) this.#nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < NEAR_BOTTOM_PX;
  }

  async loadOlder(): Promise<void> {
    const el = this.scrollerRef()?.nativeElement;
    const prevHeight = el?.scrollHeight ?? 0;
    const prevTop = el?.scrollTop ?? 0;
    await this.#chatService.loadOlder();
    // Garde la position de lecture une fois les anciens messages insérés au-dessus.
    this.#afterRender(() => {
      const target = this.scrollerRef()?.nativeElement;
      if (target) target.scrollTop = target.scrollHeight - prevHeight + prevTop;
    });
  }

  onInput(event: Event): void {
    const textarea = event.target as HTMLTextAreaElement;
    this.draft.set(textarea.value);
    this.#autoResize(textarea);
    if (textarea.value.trim()) this.#chatService.notifyTyping();
  }

  onKeydown(event: KeyboardEvent): void {
    // keyCode 229 : certains navigateurs signalent ainsi une composition IME en cours.
    if (event.key !== 'Enter' || event.shiftKey || event.isComposing || event.keyCode === 229) return;
    event.preventDefault();
    void this.submit();
  }

  async submit(): Promise<void> {
    const text = this.draft().trim();
    if (!text || !this.canSend()) return;
    const key = this.#draftKey;
    const sent = await this.#chatService.send(text);
    // Conversation changée pendant l'envoi : le brouillon courant appartient à l'autre.
    if (!sent || key !== this.#draftKey) return;
    // On ne retire que le texte envoyé : ce qui a été tapé pendant l'envoi reste.
    const current = this.draft().trimStart();
    if (current.trim() === text) {
      this.draft.set('');
    } else if (current.startsWith(text)) {
      this.draft.set(current.slice(text.length).trimStart());
    }
    this.#nearBottom = true;
    this.#afterRender(() => {
      const textarea = this.composerRef()?.nativeElement;
      if (textarea) this.#autoResize(textarea);
      this.#scrollToBottom();
    });
  }

  #resetDraft(): void {
    this.draft.set('');
    const textarea = this.composerRef()?.nativeElement;
    if (textarea) textarea.style.height = '';
  }

  #autoResize(textarea: HTMLTextAreaElement): void {
    textarea.style.height = 'auto';
    textarea.style.height = `${textarea.scrollHeight}px`;
  }

  #scrollToBottom(): void {
    const el = this.scrollerRef()?.nativeElement;
    if (el) el.scrollTop = el.scrollHeight;
    this.#nearBottom = true;
  }

  /** Navigateur uniquement : afterNextRender n'est jamais exécuté côté serveur. */
  #afterRender(fn: () => void): void {
    afterNextRender(fn, { injector: this.#injector });
  }
}
